/**
 * DailyPasscodeCard — home/email-page tile that lets a crew member scan
 * their printed register passcode and then displays the same barcode in-
 * app for the rest of the shift. All passcodes expire at midnight in the
 * store's local timezone.
 *
 * State machine:
 *   - LOADING: fetching /passcodes/me
 *   - UNCLAIMED: no active passcode → "Scan Daily Passcode" CTA
 *   - SCANNED: barcode read; waiting for receipt-date confirmation
 *   - CLAIMED: barcode rendered, tap to expand to full-screen
 *
 * Security:
 *   - Hidden entirely while the user session is mirroring another store
 *     so a DM/admin "View As" doesn't leak their own personal passcode
 *     into the shadowed view (privacy + audit safety, 2026-06-15).
 *   - After a successful barcode scan we require the user to type the
 *     date printed at the bottom of their receipt (MM/DD) before the
 *     claim hits the backend. Server re-validates against today's date
 *     in the store's local timezone — stops "found yesterday's receipt
 *     in the trash, scan it today" attacks.
 *
 * Built 2026-06-15 with the new passcode endpoints.
 */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { Ticket, X, CheckCircle, CalendarBlank, Spinner, Sun } from "@phosphor-icons/react";
import BarcodeDisplay from "./BarcodeDisplay";
import BarcodeScanner from "./BarcodeScanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Lazy-load Tesseract.js (~2MB wasm + lang) only when the user actually
 *  scans a passcode. Module-level caches so we only download + spin up
 *  the worker once per session. Returns the recognized text + an
 *  inferred M/D date if one could be parsed off the receipt, or null on
 *  failure.
 *
 *  Hardening pass 2026-02-14 (user report "OCR was supposed to be the
 *  primary path, why am I being asked to type every time?"):
 *    - Worker API with PSM 6 (single uniform block of text) — thermal
 *      receipts read MUCH better than the default PSM 3
 *    - Character whitelist limited to digits + slashes + colon + the
 *      letters in "Date" + AM/PM markers, so "/" stops being misread
 *      as "1" or "I"
 *    - Image preprocessing: grayscale + contrast boost on a canvas
 *      before sending to Tesseract so faint thermal print reads better
 *    - Multi-pattern regex (Date-prefixed M/D, plain M/D/YYYY,
 *      M/D/YY, YYYY-MM-DD ISO, bare M/D) so any common receipt date
 *      format hits
 *    - Console log of the OCR text + matched pattern so we can iterate
 *      without redeploying (user inspect during testing). */
let _tesseractWorker = null;
async function getTesseractWorker() {
  if (_tesseractWorker) return _tesseractWorker;
  const mod = await import("tesseract.js");
  const worker = await mod.createWorker("eng", undefined, {
    // Silent — no need for the progress logger to spam the console.
  });
  await worker.setParameters({
    tessedit_pageseg_mode: "6",
    // Only the characters we actually expect in a receipt date line.
    tessedit_char_whitelist: "0123456789/:-DateAMP ",
  });
  _tesseractWorker = worker;
  return worker;
}

/** Light image preprocessing — pull the data URL through a canvas,
 *  convert to grayscale, then boost contrast. Returns a new data URL.
 *  Faint thermal text often reads 2-3x better after this pass. */
async function preprocessFrame(dataUrl) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const id = ctx.getImageData(0, 0, c.width, c.height);
        const d = id.data;
        // Grayscale + simple contrast stretch around midpoint 128.
        const contrast = 1.6;
        for (let i = 0; i < d.length; i += 4) {
          const g = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          const v = Math.max(0, Math.min(255, (g - 128) * contrast + 128));
          d[i] = d[i + 1] = d[i + 2] = v;
        }
        ctx.putImageData(id, 0, 0);
        resolve(c.toDataURL("image/jpeg", 0.92));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    } catch {
      resolve(dataUrl);
    }
  });
}

/** Parse the recognized OCR text for any date-like pattern. Returns the
 *  first match as a normalized "M/D" string, or null if none found. */
function extractMDFromText(text) {
  if (!text) return null;
  // Order matters — more specific patterns first. The plain "M/D"
  // (no year) is last so we don't accidentally match "11:43" as 11/43.
  const patterns = [
    { re: /Date[:\s]+(\d{1,2})[/\-.](\d{1,2})(?:[/\-.]\d{2,4})?/i, m: 1, d: 2 },
    { re: /(\d{4})-(\d{1,2})-(\d{1,2})/, m: 2, d: 3 },
    { re: /(\d{1,2})[/\-.](\d{1,2})[/\-.](20\d{2}|\d{2})/, m: 1, d: 2 },
    { re: /\b(\d{1,2})[/\-.](\d{1,2})\b(?!\d)/, m: 1, d: 2 },
  ];
  for (const p of patterns) {
    const match = text.match(p.re);
    if (match) {
      const mm = parseInt(match[p.m], 10);
      const dd = parseInt(match[p.d], 10);
      if (mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
        return `${mm}/${dd}`;
      }
    }
  }
  return null;
}

async function ocrReceiptDate(dataUrl) {
  if (!dataUrl) return null;
  try {
    const worker = await getTesseractWorker();
    const processed = await preprocessFrame(dataUrl);
    const { data } = await worker.recognize(processed);
    const text = (data && data.text) || "";
    const md = extractMDFromText(text);
    // Console log so we can verify what OCR actually read during testing
    // without needing to redeploy. Truncated to keep the console clean.
    // eslint-disable-next-line no-console
    console.info("[passcode-ocr]", { md, text: text.replace(/\s+/g, " ").trim().slice(0, 200) });
    return { text, md };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[passcode-ocr] failed", e);
    return null;
  }
}

function expiresHoursFromNow(iso) {
  if (!iso) return "";
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "Expired";
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Today's date in "M/D" format (Denver tz so it matches the store).
 *  We don't bother with year because (a) the daily passcode is only
 *  valid today and (b) older receipts from previous years are caught
 *  by the same M/D check the next year (different M/D). */
function todayMD() {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Denver",
    month: "numeric",
    day: "numeric",
  });
  return fmt.format(new Date()); // e.g. "2/14"
}

/**
 * Translate any axios/network/Cloudflare failure from /passcodes/claim
 * into a short, user-safe sentence we can render inside the receipt-date
 * modal. Critically, NEVER pass raw upstream HTML (e.g. Cloudflare 520
 * "the origin web server sent a response that Cloudflare could not
 * parse…" pages) through to the user — see bug report 2026-06-13.
 *
 * Also console.error()s the underlying error + a tag so we can grep
 * browser logs and trace the real failure on the backend side.
 */
function describeClaimError(e) {
  // Branch 1: our own apiScope interceptor flagged a Cloudflare HTML
  // response that came back with a 2xx status code. Treat as transient.
  if (e?.code === "CLOUDFLARE_HTML") {
    // eslint-disable-next-line no-console
    console.error("[passcode-claim] Cloudflare HTML response", e);
    return "Something went wrong claiming this receipt. Please try again in a moment.";
  }
  // Branch 2: no response at all — network drop, timeout, DNS, etc.
  if (!e?.response) {
    // eslint-disable-next-line no-console
    console.error("[passcode-claim] network failure", e);
    return "Couldn't reach the server. Check your connection and try again.";
  }
  const status = e.response.status;
  const data = e.response.data;
  // Branch 3: backend HTTPException — has a clean string `detail` we can
  // surface verbatim (e.g. "That receipt isn't from today…").
  const detail = (data && typeof data === "object") ? data.detail : null;
  if (typeof detail === "string" && detail.trim() && !/[<>]/.test(detail)) {
    return detail;
  }
  // Branch 4: any other shape — HTML body, empty body, malformed JSON,
  // 5xx with no JSON, etc. Show the generic retry copy and log details.
  // eslint-disable-next-line no-console
  console.error("[passcode-claim] upstream error", { status, data, error: e });
  if (status >= 500 || status === 0) {
    return "Something went wrong claiming this receipt. Please try again in a moment.";
  }
  return "Couldn't claim that passcode. Please try again.";
}

export default function DailyPasscodeCard({
  user,
  // NEW (2026-XX): controlled-view + headless mode. Mirrors the shape
  // added to YourTasksTodayCard so HomePage.js can open the passcode
  // full-view (or scan flow if not yet claimed) from the new home
  // summary card without duplicating the dashed bar UI. Undefined
  // → falls back to internal state, preserving every existing caller.
  hideBar = false,
  open,
  onOpenChange,
  // Fired the instant a claim succeeds so a parent summary tile can flip
  // to "Ready" without waiting for its own refetch. (Bug 2026-06-21:
  // "takes a second to load on the Home Screen after you scan it in.")
  onClaimed,
}) {
  const [passcode, setPasscode] = useState(null);
  // No loading state needed in mirror mode — we never fetch.
  const isMirroring = !!user?._mirroring;
  const [loading, setLoading] = useState(!isMirroring);
  const [scanning, setScanning] = useState(false);
  const [showFullLocal, setShowFullLocal] = useState(false);
  const isControlled = typeof open === "boolean" && typeof onOpenChange === "function";
  const showFull = isControlled ? open : showFullLocal;
  const setShowFull = isControlled ? onOpenChange : setShowFullLocal;
  // After a successful camera read we stash the raw code here and prompt
  // the user to type the receipt date. Only once that matches does the
  // claim get sent. {code, dateInput, error}
  const [pendingClaim, setPendingClaim] = useState(null);

  // SECURITY: when an admin/DM is mirroring another store, never render
  // their personal daily passcode — both because it's PII for them and
  // because it confuses the mirrored-store context.

  const reload = async () => {
    if (!user?.crew_id) return;
    try {
      const r = await axios.get(`${API}/passcodes/me`, { params: { crew_id: user.crew_id } });
      setPasscode(r.data || null);
    } catch { /* swallow */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (!isMirroring) reload(); /* eslint-disable-line */ }, [user?.crew_id, isMirroring]);

  // Sync controlled `open` prop → internal `scanning` state. MUST live
  // before any early returns below so React sees this hook fire in the
  // same order every render (react-hooks/rules-of-hooks). When the
  // parent sets open=true, kick off the scan flow. When the scanner or
  // date-confirm closes, we notify the parent via `onOpenChange`.
  useEffect(() => {
    if (!isControlled || passcode) return;
    if (open && !scanning && !pendingClaim) setScanning(true);
    else if (!open && scanning) setScanning(false);
  }, [isControlled, open, scanning, pendingClaim, passcode]);

  // Camera produced a code + a small array of still frames. Try OCR on
  // each frame in turn; first hit wins. Happy path: OCR reads today's
  // date → silent auto-claim. Mismatch: hard error. No frame
  // recognized: typed prompt as a clearly-marked fallback. Either way
  // the server re-validates the date.
  const onScan = async (code, frames) => {
    setScanning(false);
    const expected = todayMD();
    const list = Array.isArray(frames) ? frames.filter(Boolean) : (frames ? [frames] : []);

    if (list.length === 0) {
      setPendingClaim({ code, dateInput: "", error: "", ocrBusy: false });
      return;
    }

    setPendingClaim({ code, dateInput: "", error: "", ocrBusy: true });
    let ocrMd = null;
    for (const frame of list) {
      // eslint-disable-next-line no-await-in-loop
      const r = await ocrReceiptDate(frame);
      if (r?.md) { ocrMd = r.md; break; }
    }

    if (ocrMd && ocrMd === expected) {
      try {
        const r = await axios.post(`${API}/passcodes/claim`, {
          crew_id: user.crew_id,
          code,
          receipt_date: ocrMd,
        });
        setPasscode(r.data);
        setPendingClaim(null);
        if (onClaimed) onClaimed(r.data);
        toast.success("Passcode locked to you for today");
        return;
      } catch (e) {
        const detail = describeClaimError(e);
        setPendingClaim({ code, dateInput: ocrMd, error: detail, ocrBusy: false });
        return;
      }
    }
    if (ocrMd && ocrMd !== expected) {
      // Lock the dialog into "stale receipt" state — the user can NOT
      // type their way past an OCR'd mismatched date by re-entering
      // today's M/D over the top (which previously let users bypass the
      // verification entirely). User must print a fresh passcode and
      // re-scan. (Bug 2026-02-14: receipt printed 6/8 but user typed
      // 6/9 and the claim only failed later on the format regex.)
      setPendingClaim({
        code,
        dateInput: ocrMd,
        error: `That receipt looks like it's from ${ocrMd}. Today is ${expected}. Print a fresh passcode and re-scan.`,
        ocrBusy: false,
        staleLocked: true,
      });
      return;
    }
    setPendingClaim({ code, dateInput: "", error: "", ocrBusy: false });
  };

  const submitClaim = async () => {
    if (!pendingClaim) return;
    const expected = todayMD();
    const raw = (pendingClaim.dateInput || "").trim();
    // Normalize both sides to M/D (strip leading zeros, ignore year).
    const norm = (s) => {
      const m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})(?:[\/\-.]\d{2,4})?$/);
      if (!m) return null;
      return `${parseInt(m[1], 10)}/${parseInt(m[2], 10)}`;
    };
    const got = norm(raw);
    if (!got) {
      setPendingClaim((p) => ({ ...p, error: "Please enter the date as M/D (e.g. 2/14)." }));
      return;
    }
    if (got !== expected) {
      setPendingClaim((p) => ({
        ...p,
        error: `That receipt isn't from today. Today is ${expected}. Print a fresh passcode.`,
      }));
      return;
    }
    try {
      const r = await axios.post(`${API}/passcodes/claim`, {
        crew_id: user.crew_id,
        code: pendingClaim.code,
        receipt_date: got, // server re-validates against today in store TZ
      });
      setPasscode(r.data);
      setPendingClaim(null);
      if (onClaimed) onClaimed(r.data);
      toast.success("Passcode locked to you for today");
    } catch (e) {
      const detail = describeClaimError(e);
      setPendingClaim((p) => ({ ...p, error: detail }));
    }
  };

  if (loading) return null;
  if (isMirroring) {
    // Render a subtle hint so the mirrored-store UI doesn't feel "broken"
    // when the passcode card is intentionally suppressed.
    return (
      <div
        className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-3 flex items-center gap-2"
        data-testid="passcode-hidden-mirror"
      >
        <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center flex-shrink-0">
          <Ticket weight="bold" className="w-5 h-5 text-slate-500" />
        </div>
        <p className="text-xs text-slate-600 leading-snug">
          Daily Passcode hidden in mirror view — personal to each crew member.
        </p>
      </div>
    );
  }

  // Unclaimed → scan CTA

  if (!passcode) {
    const notifyClose = () => {
      setScanning(false);
      if (isControlled) onOpenChange(false);
    };
    return (
      <>
        {!hideBar && (
          <button
            type="button"
            onClick={() => setScanning(true)}
            className="w-full bg-white border-2 border-dashed border-slate-300 rounded-2xl p-4 flex items-center gap-3 hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
            data-testid="passcode-scan-cta"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Ticket weight="bold" className="w-6 h-6 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-black uppercase tracking-tight text-slate-900">
                Daily Passcode
              </p>
              <p className="text-xs text-slate-500 leading-snug">
                Tap to scan your printed register passcode for today
              </p>
            </div>
            <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">SCAN</span>
          </button>
        )}
        {scanning && (
          <BarcodeScanner onScan={onScan} onClose={notifyClose} />
        )}
        {pendingClaim && (
          <DateConfirmDialog
            pending={pendingClaim}
            onCancel={() => setPendingClaim(null)}
            onChange={(v) => setPendingClaim((p) => ({ ...p, dateInput: v, error: "" }))}
            onSubmit={submitClaim}
          />
        )}
      </>
    );
  }

  // Claimed → preview tile + bottom-sheet on tap
  return (
    <>
      {!hideBar && (
        <button
          type="button"
          onClick={() => setShowFull(true)}
          className="w-full bg-white border border-slate-200 rounded-2xl p-3 flex items-center gap-2 hover:shadow-md transition-shadow text-left min-w-0"
          data-testid="passcode-claimed-tile"
        >
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <CheckCircle weight="fill" className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-black uppercase tracking-tight text-slate-900">
              Daily Passcode
            </p>
            <p className="text-[10px] text-slate-500 truncate font-mono">{passcode.code}</p>
            <p className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider">
              Expires in {expiresHoursFromNow(passcode.expires_at)}
            </p>
          </div>
          <span className="text-[11px] font-bold text-blue-600 uppercase tracking-wider flex-shrink-0">SHOW</span>
        </button>
      )}

      {showFull && (
        <BarcodeBottomSheet
          title="Daily Passcode"
          subtitle={passcode.crew_first_name || "You"}
          value={passcode.code}
          footer={`Expires in ${expiresHoursFromNow(passcode.expires_at)}`}
          onClose={() => setShowFull(false)}
          testId="passcode-fullscreen"
          barcodeTestId="passcode-barcode"
        />
      )}
    </>
  );
}

/** Tiny modal that asks the user to verify the date printed on their
 *  receipt. Pre-fills automatically when OCR succeeded, drops back to
 *  manual typing when OCR couldn't read the date. Stops "found
 *  yesterday's receipt → scan today" abuse. */
function DateConfirmDialog({ pending, onChange, onSubmit, onCancel }) {
  // Split the single "M/D" string into two values so we can render the
  // input as two side-by-side numeric fields (the iOS numeric keypad has
  // no "/" key — user report 2026-02-14). We re-combine to "M/D" before
  // pushing back up to the parent.
  const monthRef = React.useRef(null);
  const dayRef = React.useRef(null);
  const [m, d] = (pending.dateInput || "").split("/");
  const setBoth = (nm, nd) => onChange(`${nm}/${nd}`);
  return (
    <div
      className="fixed inset-0 z-[160] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="passcode-date-confirm"
    >
      <div
        className="bg-white rounded-2xl p-5 max-w-sm w-full space-y-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
            <CalendarBlank weight="bold" className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-sm font-black uppercase tracking-tight text-slate-900">
              Verify Receipt Date
            </p>
            <p className="text-[11px] text-slate-500">Stops stale receipts from being claimed</p>
          </div>
        </div>
        {pending.ocrBusy ? (
          <div className="flex items-center justify-center gap-2 py-6" data-testid="passcode-date-ocr-busy">
            <Spinner weight="bold" className="w-5 h-5 text-blue-600 animate-spin" />
            <p className="text-sm font-semibold text-slate-700">Reading the date off your receipt…</p>
          </div>
        ) : pending.staleLocked ? (
          // Hard-stop: OCR confirmed the receipt isn't from today. No
          // typed override allowed — the only way out is to cancel and
          // print a fresh passcode.
          <div className="py-2" data-testid="passcode-stale-lock">
            <p className="text-sm font-bold text-rose-700 leading-snug text-center">
              {pending.error}
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-700 leading-snug">
              Look at the bottom of your printed receipt and enter the date.
              Today is <span className="font-mono font-bold">{todayMD()}</span>.
            </p>
            <div className="flex items-center gap-2 justify-center">
              <div className="flex-1">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 text-center">Month</label>
                <input
                  ref={monthRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={2}
                  autoFocus
                  value={m || ""}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 2);
                    setBoth(v, d || "");
                    // Auto-advance to day when the month is unambiguous
                    // (2 digits typed, OR a single digit that can't grow
                    // into a valid month like 2-9).
                    if (v.length === 2 || (v.length === 1 && parseInt(v, 10) >= 2)) {
                      dayRef.current?.focus();
                    }
                  }}
                  placeholder="MM"
                  className="w-full text-center font-mono text-3xl font-black tracking-wider py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                  data-testid="passcode-date-month"
                />
              </div>
              <span className="text-3xl font-black text-slate-400 mt-5">/</span>
              <div className="flex-1">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 text-center">Day</label>
                <input
                  ref={dayRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={2}
                  value={d || ""}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 2);
                    setBoth(m || "", v);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onSubmit();
                    if (e.key === "Backspace" && !d) monthRef.current?.focus();
                  }}
                  placeholder="DD"
                  className="w-full text-center font-mono text-3xl font-black tracking-wider py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                  data-testid="passcode-date-day"
                />
              </div>
            </div>
            {/* Hidden legacy input retained so existing tests that key on
                `passcode-date-input` can still locate a value. Mirrors the
                combined "M/D" string. Not visible. */}
            <input
              type="hidden"
              value={pending.dateInput || ""}
              data-testid="passcode-date-input"
              readOnly
            />
          </>
        )}
        {pending.error && !pending.staleLocked && (
          <p className="text-xs text-rose-600 font-semibold text-center" data-testid="passcode-date-error">
            {pending.error}
          </p>
        )}
        {!pending.ocrBusy && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-700 font-bold uppercase tracking-wider text-xs"
              data-testid="passcode-date-cancel"
            >
              {pending.staleLocked ? "OK" : "Cancel"}
            </button>
            {!pending.staleLocked && (
              <button
                type="button"
                onClick={onSubmit}
                disabled={!m || !d}
                className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-bold uppercase tracking-wider text-xs disabled:bg-slate-300"
                data-testid="passcode-date-confirm-btn"
              >
                Claim
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Reusable bottom-sheet that keeps the page header reachable so the
 *  user always has a way to navigate Home (fix for 2026-06-15 report
 *  "barcode overpowers the page, can't get back home"). Sized to its
 *  natural content with a `max-h-[85vh]` safety cap and an internal
 *  scroll if it ever overflows.
 *
 *  Rendered via portal to `document.body` because some host pages
 *  (CrewTallyPage) wrap their headers in `backdrop-filter`, which per
 *  CSS spec creates a containing block for `position: fixed` children.
 *  Without the portal the sheet would anchor to that header instead of
 *  the viewport, hiding 90% of the content above the visible area
 *  (2026-02-14 user report). */
export function BarcodeBottomSheet({ title, subtitle, value, footer, extra, onClose, testId, barcodeTestId }) {
  // SCAN BOOST MODE (2026-06-18) — full-screen pure-white sheet for
  // displaying a barcode at maximum panel emission. Used by:
  //   • DailyPasscodeCard — show today's register passcode at the POS
  //   • CouponHeaderPill  — show a saved coupon barcode at checkout
  //
  // Why full-screen pure white?
  // Crew using privacy-screen filters (~50% transmission loss in
  // side-view) reported the barcode scanner couldn't read it unless
  // they manually cranked brightness to 100%. We CANNOT
  // programmatically set hardware brightness from the browser —
  // there is no `navigator.setBrightness` API on iOS Safari, iOS
  // PWAs, or Android Chrome; it's a sandbox limit, not missing
  // implementation. Best-effort equivalents we DO have:
  //   1. Fill viewport with pure-white pixels → panel emission jumps
  //      to ~100% of whatever brightness is set (vs ~60% when the
  //      old sheet sat on a dim `bg-black/40` backdrop). Effective
  //      brightness through privacy films goes way up.
  //   2. `navigator.wakeLock.request('screen')` — stops iOS from
  //      auto-dimming while the user holds their phone at the
  //      scanner. Released on unmount → device returns to its normal
  //      auto-dim schedule (honest equivalent of "restore brightness
  //      to where it was before").
  //   3. Barcode rendered at 150px height (was 96px) + 3px bar width
  //      so scanners have more quiet zone and easier read.
  //   4. One-time helper toast for users with privacy screens who
  //      still need the OS slider too.
  const wakeLockRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    async function acquire() {
      try {
        if ("wakeLock" in navigator) {
          const lock = await navigator.wakeLock.request("screen");
          if (cancelled) {
            // Promise-style .catch so iOS doesn't surface the
            // rejection as an "unhandled" Script error.
            try {
              const p = lock.release();
              if (p && typeof p.catch === "function") p.catch(() => {});
            } catch { /* noop */ }
            return;
          }
          wakeLockRef.current = lock;
        }
      } catch { /* permission denied or unsupported — silent fallback to white-only boost */ }
    }
    acquire();
    // iOS releases the lock on tab/background — re-acquire on
    // visibilitychange back to visible.
    const onVis = () => { if (document.visibilityState === "visible" && !wakeLockRef.current) acquire(); };
    document.addEventListener("visibilitychange", onVis);

    // Brightness hint — once per device.
    try {
      const seen = localStorage.getItem("scanBoostBrightnessTipSeen") === "1";
      if (!seen) {
        toast("Tip · bump brightness to max for best scan", { icon: "💡", duration: 4500 });
        localStorage.setItem("scanBoostBrightnessTipSeen", "1");
      }
    } catch { /* localStorage unavailable — non-critical */ }

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      const lock = wakeLockRef.current;
      wakeLockRef.current = null;
      // lock.release() returns a Promise on iOS/Safari (not sync). If
      // we don't .catch() it, a rejection on unmount surfaces as an
      // unhandled "Script error" in the dev overlay on iPad. (Bug
      // fix 2026-06-18 — user reported runtime error overlay on the
      // Zone Sheet print page after the wake lock was added.)
      if (lock && typeof lock.release === "function") {
        try {
          const p = lock.release();
          if (p && typeof p.catch === "function") p.catch(() => { /* noop */ });
        } catch { /* sync throw on older browsers — also ignore */ }
      }
    };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[150] bg-white flex flex-col animate-[fadeIn_150ms_ease-out]"
      data-testid={testId}
    >
      {/* Minimal top bar — Sun icon hints at why the screen just
          went pure-white. */}
      <div className="shrink-0 px-5 pt-[max(env(safe-area-inset-top),0.75rem)] pb-2 flex items-start justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <Sun weight="fill" className="w-3.5 h-3.5 text-amber-500" aria-hidden />
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-600">{title}</p>
          </div>
          <p className="text-lg font-black text-slate-900 truncate">{subtitle}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 active:scale-95 transition-transform"
          aria-label="Close"
          data-testid={`${testId}-close`}
        >
          <X weight="bold" className="w-4 h-4 text-slate-700" />
        </button>
      </div>
      {/* Barcode — centered + oversized. flex-1 fills the available
          vertical space; the white background AROUND the barcode is
          what actually drives the brightness boost. */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-[480px]">
          <BarcodeDisplay value={value} height={150} width={3} testId={barcodeTestId} />
        </div>
      </div>
      {/* Footer — kept small/muted so it doesn't eat barcode white-
          space. Done button respects safe-area for notched phones. */}
      <div className="shrink-0 px-5 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-2">
        {extra}
        {footer && (
          <p className="text-center text-[11px] text-slate-500 uppercase tracking-wider font-bold pb-2">{footer}</p>
        )}
        <button
          type="button"
          onClick={onClose}
          className="w-full py-3.5 rounded-2xl bg-slate-900 text-white font-black uppercase tracking-wider text-sm hover:bg-slate-800 active:scale-[0.99] transition-all"
          data-testid={`${testId}-done`}
        >
          Done
        </button>
      </div>
    </div>,
    document.body,
  );
}
